import { get } from "lodash";
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "urql";
import { ProductCardWithQuickAdd } from "../../components/ProductCardWithQuickAdd";
import { CategoryFilter } from "../../components/CategoryFilter";
import { CategoryBanner } from "../../components/CategoryBanner";
import { Toast } from "../../components/Toast";
import {
  Product,
  Category,
} from "../../types/ProductType";
import { AvoRedApp } from "../../components/Layout/AvoRedApp";
import {
  PRODUCTS_BY_CATEGORY_QUERY,
} from "../../graphql/queries/categoryQuery";

// Updated query to include sub_categories, parent and __typename for better caching
const GetCategory = `
query GetCategory($slug: String!) {
  category(slug: $slug) {
      id
      name
      slug
      meta_title
      meta_description
      image_path
      banner_path
    parent {
      id
      name
      slug
      parent {
        id
        name
        slug
        parent {
          id
          name
          slug
        }
      }
    }
    children {
      id
      name
      slug
      __typename
    }
    products(first: 10, page: 1) {
      data {
        id
        name
        slug
        main_image_url
        price
        final_price
        variants {
          id
          name
          sku
          price
          final_price
          stock
          __typename
        }
        __typename
      }
      pagination {
        current_page
        has_more_pages
        total
        per_page
        __typename
      }
    }
    __typename
  }
}
`;

// Helper function to build breadcrumb path from category (only parent categories, not current)
const buildBreadcrumbPath = (category: any): Array<{ name: string; slug: string }> => {
  const path: Array<{ name: string; slug: string }> = [];
  let current: any = category?.parent;
  
  // Build path by traversing parent chain (only parents, not current category)
  while (current) {
    path.unshift({ name: current.name, slug: current.slug });
    current = current.parent;
  }
  
  return path;
};

export const CategoryShow = () => {
  let { slug } = useParams();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null | undefined>(
    undefined
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(9);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined); // 'price' | 'name' | 'latest'
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // Input values (for user input)
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  // Applied filter values (used in GraphQL query)
  const [appliedMinPrice, setAppliedMinPrice] = useState<string>("");
  const [appliedMaxPrice, setAppliedMaxPrice] = useState<string>("");
  const [appliedProductName, setAppliedProductName] = useState<string>("");
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: "success" | "error";
  }>({
    isVisible: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    setSelectedCategoryId(undefined);
    setCurrentPage(1);
    setSortBy(undefined);
    setSortDir("asc");
    setMinPrice("");
    setMaxPrice("");
    setProductName("");
    setAppliedMinPrice("");
    setAppliedMaxPrice("");
    setAppliedProductName("");
  }, [slug]);

  const [
    { fetching: categoryFetching, data: categoryData, error: categoryError },
  ] = useQuery({
    query: GetCategory,
    variables: { slug },
    requestPolicy: 'network-only',
  });

  const currentCategoryId = categoryData?.category?.slug === slug 
    ? categoryData?.category?.id 
    : null;

  const [
    { fetching: productsFetching, data: productsData, error: productsError },
  ] = useQuery({
    query: PRODUCTS_BY_CATEGORY_QUERY,
    variables: {
      category_id: selectedCategoryId || currentCategoryId,
      page: currentPage,
      first: perPage,
      sort_by: sortBy,
      sort_dir: sortBy && sortBy !== 'latest' ? sortDir : undefined,
      min_price: appliedMinPrice ? parseFloat(appliedMinPrice) : undefined,
      max_price: appliedMaxPrice ? parseFloat(appliedMaxPrice) : undefined,
      name: appliedProductName || undefined,
    },
    pause: !currentCategoryId && !selectedCategoryId,
    requestPolicy: "network-only",
  });

  useEffect(() => {
    const currentCategoryId = categoryData?.category?.slug === slug 
      ? categoryData?.category?.id 
      : null;
    if (currentCategoryId && selectedCategoryId === undefined) {
      setSelectedCategoryId(currentCategoryId);
    }
  }, [categoryData, selectedCategoryId, slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId]);

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategoryId(categoryId);
  };

  const handleAddToCart = (success: boolean, message: string) => {
    setToast({
      isVisible: true,
      message,
      type: success ? "success" : "error",
    });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFilters = () => {
    setAppliedMinPrice(minPrice);
    setAppliedMaxPrice(maxPrice);
    setAppliedProductName(productName);
    setCurrentPage(1);
  };

  const category = categoryData?.category?.slug === slug ? categoryData?.category : null;
  const breadcrumbPath = category ? buildBreadcrumbPath(category) : [];
  const productsResponse = productsData?.productsByCategory;
  const products =
    productsResponse?.data || (category ? get(categoryData, "category.products.data", []) : []);
  const paginatorInfoRaw = productsResponse?.pagination;
  const paginatorInfo = paginatorInfoRaw && {
    current_page: paginatorInfoRaw.current_page ?? paginatorInfoRaw.currentPage,
    per_page: paginatorInfoRaw.per_page ?? paginatorInfoRaw.perPage,
    total: paginatorInfoRaw.total,
    has_more_pages:
      paginatorInfoRaw.has_more_pages ?? paginatorInfoRaw.hasMorePages,
  };

  // Loading state
  if (categoryFetching) {
    return (
      <AvoRedApp>
        <div className="min-h-screen bg-gray-50">
          {/* Breadcrumb Skeleton */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2 text-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <span className="text-gray-300">/</span>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <span className="text-gray-300">/</span>
                <div className="h-4 bg-gray-200 rounded w-28"></div>
              </div>
            </div>
          </nav>

          {/* Header Skeleton */}
          <div className="bg-white shadow">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 animate-pulse">
              <div className="h-9 bg-gray-200 rounded w-1/3 mb-3"></div>
              <div className="h-5 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>

          {/* Main Content Skeleton */}
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar Skeleton */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                  <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-5 py-4 border-b border-gray-200">
                    <div className="h-6 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="p-4 space-y-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Products Grid Skeleton */}
              <div className="lg:col-span-3">
                {/* Sort & Filters Skeleton */}
                <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-5 animate-pulse">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-5 bg-gray-200 rounded w-20"></div>
                      <div className="h-10 bg-gray-200 rounded-lg w-40"></div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
                      <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
                      <div className="h-10 bg-gray-200 rounded-lg w-24"></div>
                    </div>
                  </div>
                </div>

                {/* Products Skeleton */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                      <div className="h-48 bg-gray-200"></div>
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AvoRedApp>
    );
  }

  // Error state
  if (categoryError || productsError) {
    return (
      <AvoRedApp>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Có lỗi xảy ra
            </h2>
            <p className="text-gray-600 mb-4">
              {categoryError?.message ||
                productsError?.message ||
                "Không thể tải dữ liệu"}
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Về trang chủ
            </Link>
          </div>
        </div>
      </AvoRedApp>
    );
  }

  // Category not found
  if (!category) {
    return (
      <AvoRedApp>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Không tìm thấy danh mục
            </h2>
            <p className="text-gray-600 mb-4">
              Danh mục bạn tìm kiếm không tồn tại.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Về trang chủ
            </Link>
          </div>
        </div>
      </AvoRedApp>
    );
  }

  return (
    <AvoRedApp>
      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center space-x-2 text-sm">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-indigo-600 transition-colors duration-200 flex items-center"
              >
                <svg 
                  className="w-4 h-4 mr-1" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                  />
                </svg>
                Trang chủ
              </Link>
              {breadcrumbPath.map((item) => (
                <React.Fragment key={item.slug}>
                  <span className="text-gray-400">/</span>
                  <Link 
                    to={`/category/${item.slug}`}
                    className="text-gray-500 hover:text-indigo-600 transition-colors duration-200"
                  >
                    {item.name}
                  </Link>
                </React.Fragment>
              ))}
              <span className="text-gray-400">/</span>
              <span className="text-gray-900 font-medium">{category.name}</span>
            </div>
          </div>
        </nav>

        {/* Category Banner */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          {category.parent && (
            <div className="mb-4">
              <Link
                to={`/category/${category.parent.slug}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors duration-200 group"
              >
                <svg 
                  className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M15 19l-7-7 7-7" 
                  />
                </svg>
                <span>Quay lại {category.parent.name}</span>
              </Link>
            </div>
          )}
          <CategoryBanner category={category} height="h-64" />
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Category Filter */}
            <div className="lg:col-span-1">
              {category.children && category.children.length > 0 && (
                <CategoryFilter
                  categories={category.children}
                  currentCategoryId={selectedCategoryId === null ? undefined : selectedCategoryId}
                  onCategorySelect={handleCategorySelect}
                  parentCategoryName={category.name}
                />
              )}
            </div>

            {/* Main Content - Products */}
            <div className="lg:col-span-3">
              {/* Sort & Filters */}
              <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Sort Section */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <svg 
                        className="w-5 h-5 text-gray-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" 
                        />
                      </svg>
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Sắp xếp:
                      </label>
                    </div>
                    <select
                      className="border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow shadow-sm hover:shadow-md min-w-[140px]"
                      value={sortBy || ""}
                      onChange={(e) => setSortBy(e.target.value || undefined)}
                    >
                      <option value="">Mặc định</option>
                      <option value="latest">Mới nhất</option>
                      <option value="name">Tên A-Z</option>
                      <option value="price">Giá</option>
                    </select>
                    {sortBy && sortBy !== 'latest' && (
                      <select
                        className="border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow shadow-sm hover:shadow-md min-w-[130px]"
                        value={sortDir}
                        onChange={(e) =>
                          setSortDir(e.target.value as "asc" | "desc")
                        }
                      >
                        <option value="asc">Tăng dần ↑</option>
                        <option value="desc">Giảm dần ↓</option>
                      </select>
                    )}
                  </div>

                  {/* Filters Section */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Product Name Filter */}
                    <div className="relative flex-1 min-w-[150px]">
                      <svg 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                        />
                      </svg>
                      <input
                        type="text"
                        placeholder="Tìm theo tên sản phẩm"
                        className={`w-full border rounded-lg pl-10 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm ${
                          productsFetching 
                            ? 'border-indigo-300 bg-indigo-50 text-gray-600 cursor-wait' 
                            : 'border-gray-300 text-gray-700 hover:shadow-md focus:shadow-md'
                        }`}
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        disabled={productsFetching}
                      />
                    </div>
                    <div className="relative flex-1 min-w-[110px]">
                      <svg 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <input
                        type="number"
                        placeholder="Giá từ"
                        className={`w-full border rounded-lg pl-10 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm ${
                          productsFetching 
                            ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-wait' 
                            : 'border-gray-300 text-gray-700 hover:shadow-md focus:shadow-md'
                        }`}
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        disabled={productsFetching}
                      />
                    </div>
                    <div className="relative flex-1 min-w-[110px]">
                      <svg 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <input
                        type="number"
                        placeholder="đến"
                        className={`w-full border rounded-lg pl-10 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm ${
                          productsFetching 
                            ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-wait' 
                            : 'border-gray-300 text-gray-700 hover:shadow-md focus:shadow-md'
                        }`}
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        disabled={productsFetching}
                      />
                    </div>
                    <button
                      onClick={applyFilters}
                      disabled={productsFetching}
                      className={`bg-gradient-to-r text-white text-sm font-medium px-6 py-2 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                        productsFetching
                          ? 'from-gray-400 to-gray-500 cursor-wait opacity-60'
                          : 'from-indigo-600 to-blue-600 hover:shadow-lg hover:from-indigo-700 hover:to-blue-700'
                      }`}
                    >
                      <svg 
                        className="w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" 
                        />
                      </svg>
                      Áp dụng
                    </button>
                  </div>
                </div>
              </div>
              {productsFetching ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mx-auto h-24 w-24 text-gray-400">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    Không có sản phẩm
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Không có sản phẩm nào trong danh mục này.
                  </p>
                </div>
              ) : (
                <>
                  {/* Products Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product: Product) => (
                      <ProductCardWithQuickAdd
                        key={product.id}
                        product={product}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {paginatorInfo &&
                    paginatorInfo.total > paginatorInfo.per_page && (
                      <div className="mt-12 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Hiển thị{" "}
                          <span className="font-medium">
                            {(paginatorInfo.current_page - 1) *
                              paginatorInfo.per_page +
                              1}
                          </span>{" "}
                          đến{" "}
                          <span className="font-medium">
                            {Math.min(
                              paginatorInfo.current_page *
                                paginatorInfo.per_page,
                              paginatorInfo.total
                            )}
                          </span>{" "}
                          trong tổng số{" "}
                          <span className="font-medium">
                            {paginatorInfo.total}
                          </span>{" "}
                          sản phẩm
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Trước
                          </button>

                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md">
                            Trang {currentPage}
                          </span>

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!paginatorInfo.has_more_pages}
                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Sau
                          </button>
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={handleCloseToast}
      />
    </AvoRedApp>
  );
};
